// The battle screen: a side view like the SNES Final Fantasy games. Creatures on the left, the party on the
// right, blue menu windows below with everyone's health and time bar. When your bar fills, your menu opens and
// the battle waits for you. Everything you see is what the server decided; this only plays it out.
import { ATB_FULL, SKILLS, CHOOSE_TICKS, formationDef, type BattleEvent, type BattleView, type Fighter } from '../../../shared/battle.js';
import { drawMob, mobTop } from '../render/mobs.js';
import type { Look } from '../../../shared/look.js';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const W = 224, H = 132;
type Mode = 'i' | 'w' | 'a';
export interface BattleHost {
  me(): number;
  look(id: number): Look | null;
  sprite(key: number, look: Look, dir: string, mode: Mode, fi: number): HTMLCanvasElement | null;
  /** The zone's ground and a dry spot near the fight to paint the battlefield from. */
  backdrop(x: number, y: number): { cv: HTMLCanvasElement; x: number; y: number } | null;
  night(): number;                 // 0 day .. 1 night
  potions(): number;
  send(cmd: string, target?: number, skill?: string): void;
  sound(name: 'hit' | 'hurt' | 'poof' | 'dodge' | 'level' | 'open' | 'close' | 'join' | 'error' | 'loot' | 'battle' | 'chain', pitch?: number): void;
  /** The world as it is on screen right now, and where you are on it (canvas pixels), for the intro. */
  snapshot(): { cv: HTMLCanvasElement; x: number; y: number } | null;
}
const INTRO_MS = 700, INTRO_FADE_MS = 380;

interface Fx { t0: number; dur: number; kind: 'lunge' | 'flash' | 'num' | 'fade'; who: number; to?: number; text?: string; color?: string }

export class BattleScreen {
  active = false;
  private view = $<HTMLCanvasElement>('battleView'); private ctx = this.view.getContext('2d')!;
  private b: BattleView | null = null;
  private queue: BattleEvent[] = []; private playing = 0;
  private fx: Fx[] = [];
  private banner = ''; private bannerUntil = 0;
  private menu: 'main' | 'skill' | 'item' | 'target-foe' | 'target-ally' | null = null;
  private pending: { cmd: string; skill?: string } | null = null;
  private turnSince = 0; private myTurn = false;
  private down = new Map<number, number>();    // fighter -> when they fell (for the fade)
  private ending: { result: string; text: string; at: number } | null = null;
  onClose: () => void = () => {};

  constructor(private host: BattleHost) {
    this.view.width = W; this.view.height = H;
    $('bHelp').addEventListener('click', () => this.host.send('help'));
    $('bCancelRun').addEventListener('click', () => this.host.send('cancel_run'));
    $('bLeave').addEventListener('click', () => this.host.send('leave'));
    $('bResult').addEventListener('click', () => this.close());
    addEventListener('keydown', e => {
      if (!this.active) return;
      if (this.ending && (e.code === 'Space' || e.code === 'Enter' || e.code === 'Escape')) { e.preventDefault(); this.close(); return; }
      if (e.code === 'Escape' && this.menu && this.menu !== 'main') { e.preventDefault(); this.openMenu('main'); return; }
      const n = Number(e.key);
      if (n >= 1 && n <= 9) { const btns = [...$('bMenu').querySelectorAll<HTMLButtonElement>('button:not([disabled])')]; if (btns[n - 1]) { e.preventDefault(); btns[n - 1].click(); } }
      if (['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'KeyW', 'KeyA', 'KeyS', 'KeyD'].includes(e.code)) {
        const btns = [...$('bMenu').querySelectorAll<HTMLButtonElement>('button:not([disabled])')]; if (!btns.length) return;
        e.preventDefault(); e.stopPropagation();
        const i = btns.indexOf(document.activeElement as HTMLButtonElement), d = ['ArrowDown', 'ArrowRight', 'KeyS', 'KeyD'].includes(e.code) ? 1 : -1;
        btns[(i + d + btns.length) % btns.length].focus();
      }
    }, true);
  }

  /** New state from the server, with what just happened. */
  update(m: { battle: BattleView; events: BattleEvent[]; start?: boolean }): void {
    if (!this.active) this.open();
    this.b = m.battle;
    this.queue.push(...m.events);
    for (const f of m.battle.fighters) if (f.state !== 'down') this.down.delete(f.id);
    const mine = m.battle.turn === this.host.me();
    if (mine && !this.myTurn) { this.turnSince = performance.now(); this.openMenu('main'); this.host.sound('open'); }
    if (!mine && this.myTurn) this.openMenu(null);
    this.myTurn = mine;
    this.panels();
  }

  end(m: { result: string; text: string }): void {
    this.ending = { result: m.result, text: m.text, at: performance.now() };
    this.openMenu(null);
    if (m.result === 'left') { this.close(); return; }
    const r = $('bResult'); r.hidden = false;
    r.innerHTML = '';
    const t = document.createElement('b'); t.textContent = m.text; r.append(t);
    const s = document.createElement('small'); s.textContent = m.result === 'win' ? 'Tap or press Space to carry on' : m.result === 'fled' ? 'Safe for now' : 'You wake up by the wayshrine…'; r.append(s);
    this.host.sound(m.result === 'win' ? 'level' : m.result === 'fled' ? 'dodge' : 'close');
    setTimeout(() => { if (this.ending) this.close(); }, m.result === 'win' ? 4500 : 2500);
  }

  private open(): void {
    this.active = true; this.ending = null; this.queue = []; this.fx = []; this.down.clear(); this.banner = '';
    $('bResult').hidden = true; document.body.classList.add('in-battle');
    // the SNES moment: the world pixelates and swirls in on you, flashes, and the battle is there
    const snap = this.host.snapshot();
    if (snap) { this.intro = { ...snap, t0: performance.now() }; const fx = $<HTMLCanvasElement>('battleFx'); fx.width = snap.cv.width; fx.height = snap.cv.height; fx.hidden = false; $('battle').hidden = true; }
    else $('battle').hidden = false;
    this.host.sound('battle');
  }
  private intro: { cv: HTMLCanvasElement; x: number; y: number; t0: number } | null = null;
  private mosaic: HTMLCanvasElement = document.createElement('canvas');
  private drawIntro(now: number): void {
    const it = this.intro!, fx = $<HTMLCanvasElement>('battleFx'), c = fx.getContext('2d')!, W2 = fx.width, H2 = fx.height;
    const t = now - it.t0;
    if (t >= INTRO_MS && $('battle').hidden) $('battle').hidden = false;          // the battle is underneath now
    if (t >= INTRO_MS + INTRO_FADE_MS) { fx.hidden = true; this.intro = null; return; }
    if (t < INTRO_MS) {
      const k = t / INTRO_MS, block = Math.max(1, Math.round(1 + Math.pow(k, 1.6) * Math.min(W2, H2) / 14));
      const m = this.mosaic, mw = Math.max(1, Math.ceil(W2 / block)), mh = Math.max(1, Math.ceil(H2 / block));
      if (m.width !== mw || m.height !== mh) { m.width = mw; m.height = mh; }
      const g = m.getContext('2d')!; g.imageSmoothingEnabled = true;
      g.setTransform(1, 0, 0, 1, 0, 0); g.fillStyle = '#000'; g.fillRect(0, 0, mw, mh);
      // zoom and swirl in around you
      const zoom = 1 + k * k * 1.4, turn = k * k * 1.1;
      g.translate(it.x / block, it.y / block); g.rotate(turn); g.scale(zoom, zoom); g.translate(-it.x / block, -it.y / block);
      g.drawImage(it.cv, 0, 0, W2, H2, 0, 0, W2 / block, H2 / block);
      c.imageSmoothingEnabled = false; c.setTransform(1, 0, 0, 1, 0, 0);
      c.drawImage(m, 0, 0, mw, mh, 0, 0, mw * block, mh * block);
      if (k > 0.55) { c.fillStyle = `rgba(255,255,255,${Math.min(1, (k - 0.55) / 0.45)})`; c.fillRect(0, 0, W2, H2); }
    } else {
      c.clearRect(0, 0, W2, H2);
      c.fillStyle = `rgba(255,255,255,${1 - (t - INTRO_MS) / INTRO_FADE_MS})`; c.fillRect(0, 0, W2, H2);
    }
  }
  close(): void {
    if (!this.active) return;
    this.intro = null; $('battleFx').hidden = true;
    this.active = false; this.b = null; this.ending = null; this.myTurn = false; this.menu = null;
    $('battle').hidden = true; document.body.classList.remove('in-battle');
    this.onClose();
  }

  // ---------- menus ----------
  private openMenu(which: BattleScreen['menu']): void {
    this.menu = which;
    const box = $('bMenu'); box.replaceChildren(); box.hidden = !which;
    if (!which || !this.b) return;
    const btn = (label: string, on: () => void, disabled = false, hint = '') => {
      const b = document.createElement('button'); b.type = 'button'; b.textContent = label; b.disabled = disabled; if (hint) b.title = hint;
      b.addEventListener('click', on); box.append(b); return b;
    };
    const foes = this.b.fighters.filter(f => f.side === 'foe' && f.hp > 0), allies = this.b.fighters.filter(f => f.side === 'party' && f.state !== 'down');
    const pick = (cmd: string, skill?: string, who: 'foe' | 'ally' = 'foe') => {
      const list = who === 'foe' ? foes : allies;
      if (who === 'foe' && list.length === 1) return this.choose(cmd, list[0].id, skill);
      this.pending = { cmd, skill }; this.openMenu(who === 'foe' ? 'target-foe' : 'target-ally');
    };
    if (which === 'main') {
      const me = this.b.fighters.find(f => f.id === this.host.me());
      btn('Attack', () => pick('attack')).focus();
      btn('Skill', () => this.openMenu('skill'));
      btn(`Item (${this.host.potions()})`, () => this.openMenu('item'), this.host.potions() <= 0);
      btn('Defend', () => this.choose('defend'));
      btn(me?.helper ? 'Run (leave)' : 'Run', () => me?.helper ? this.host.send('leave') : this.choose('run'), false, me?.helper ? 'Helpers can leave whenever they like' : 'Everyone in your party has to choose Run');
    } else if (which === 'skill') {
      for (const s of SKILLS) btn(s.name, () => pick('skill', s.id, s.target), false, s.desc);
      btn('Back', () => this.openMenu('main'));
      (box.firstElementChild as HTMLButtonElement)?.focus();
    } else if (which === 'item') {
      btn(`Small Potion ×${this.host.potions()}`, () => pick('item', undefined, 'ally'), this.host.potions() <= 0, 'Restores 30 health');
      btn('Back', () => this.openMenu('main'));
      (box.firstElementChild as HTMLButtonElement)?.focus();
    } else {
      for (const f of which === 'target-foe' ? foes : allies) btn(`${f.name}  ${f.hp}/${f.max}`, () => this.choose(this.pending!.cmd, f.id, this.pending!.skill));
      btn('Back', () => this.openMenu('main'));
      (box.firstElementChild as HTMLButtonElement)?.focus();
    }
  }
  private choose(cmd: string, target?: number, skill?: string): void { this.host.send(cmd, target, skill); this.openMenu(null); this.myTurn = false; }

  private panels(): void {
    const b = this.b; if (!b) return;
    const row = (f: Fighter) => {
      const d = document.createElement('div'); d.className = 'b-row' + (f.state === 'down' ? ' down' : '') + (b.turn === f.id ? ' turn' : '') + (f.id === this.host.me() ? ' me' : '');
      const n = document.createElement('span'); n.className = 'b-name'; n.textContent = (f.side === 'foe' ? '' : f.state === 'run' ? '🏃 ' : f.state === 'defend' ? '🛡 ' : '') + f.name + (f.helper ? ' (helping)' : '') + (f.away ? ' (away)' : '');
      d.append(n);
      if (f.side === 'party') {
        const hp = document.createElement('span'); hp.className = 'b-hp' + (f.hp / f.max <= 0.25 ? ' low' : ''); hp.textContent = `${f.hp}/${f.max}`;
        const bar = document.createElement('span'); bar.className = 'b-atb' + (f.atb >= ATB_FULL ? ' full' : ''); const fill = document.createElement('i'); fill.style.width = `${Math.round(f.atb / ATB_FULL * 100)}%`; bar.append(fill);
        d.append(hp, bar);
      } else { const lv = document.createElement('span'); lv.className = 'b-lv'; lv.textContent = `Lv${f.lv}`; d.append(lv); }
      return d;
    };
    $('bFoes').replaceChildren(...b.fighters.filter(f => f.side === 'foe' && f.hp > 0).map(row));
    $('bParty').replaceChildren(...b.fighters.filter(f => f.side === 'party').map(row));
    const fd = formationDef(b.formation); $('bForm').textContent = `${fd.icon}  ${fd.name}${b.chain >= 2 ? `  ·  chain ×${b.chain}` : ''}`; $('bForm').title = fd.perks.join(' · ');
    const me = b.fighters.find(f => f.id === this.host.me());
    $('bHelp').hidden = !!me?.helper; $('bHelp').textContent = b.help ? 'Help is on: strangers can join' : 'Call for help';
    $('bHelp').classList.toggle('on', b.help);
    $('bLeave').hidden = !me?.helper;
    $('bCancelRun').hidden = me?.state !== 'run';
    const run = $('bRun');
    run.hidden = b.runners === 0;
    run.textContent = b.runners < b.needRun ? `Ready to run (${b.runners}/${b.needRun}): everyone has to choose Run` : `Everyone's running! ${Math.round(b.runOdds * 100)}% chance to get away`;
  }

  // ---------- playing it out ----------
  private step(now: number): void {
    if (now < this.playing || !this.queue.length) return;
    const e = this.queue.shift()!;
    const say = (t: string, ms = 1300) => { this.banner = t; this.bannerUntil = now + ms; };
    const name = (id: number) => this.b?.fighters.find(f => f.id === id)?.name ?? '';
    switch (e.k) {
      case 'hit': {
        const big = e.crit || e.skill === 'power';
        this.fx.push({ t0: now, dur: 360, kind: 'lunge', who: e.from, to: e.to }, { t0: now + 180, dur: 260, kind: 'flash', who: e.to }, { t0: now + 180, dur: 900, kind: 'num', who: e.to, text: e.crit ? `${e.amt}!` : String(e.amt), color: e.crit ? '#ffe9a8' : '#ffffff' });
        if (e.skill === 'power') say(`${name(e.from)}: Power Strike!`, 900);
        const foeHit = this.b?.fighters.find(f => f.id === e.to)?.side === 'foe';
        setTimeout(() => this.host.sound(foeHit ? 'hit' : 'hurt'), 180);
        this.playing = now + (big ? 650 : 520); break;
      }
      case 'miss': this.fx.push({ t0: now, dur: 360, kind: 'lunge', who: e.from, to: e.to }, { t0: now + 180, dur: 800, kind: 'num', who: e.to, text: 'miss', color: '#a3def4' }); this.host.sound('dodge'); this.playing = now + 520; break;
      case 'heal': this.fx.push({ t0: now, dur: 900, kind: 'num', who: e.to, text: `+${e.amt}`, color: '#8fe08a' }); say(e.item ? `${name(e.from)} used a potion.` : `${name(e.from)} cast Mend.`, 900); this.host.sound('loot'); this.playing = now + 600; break;
      case 'defend': say(`${name(e.who)} braces.`, 800); this.playing = now + 350; break;
      case 'run': say(e.on ? `${name(e.who)} wants to run!` : `${name(e.who)} stays to fight.`, 1000); this.playing = now + 350; break;
      case 'flee': say(e.ok ? 'Got away!' : "Couldn't get away!", 1100); if (!e.ok) this.host.sound('error'); this.playing = now + 700; break;
      case 'down': this.down.set(e.who, now); this.fx.push({ t0: now, dur: 600, kind: 'fade', who: e.who }); if (this.b?.fighters.find(f => f.id === e.who)?.side === 'foe') this.host.sound('poof'); this.playing = now + 450; break;
      case 'join': say(`${name(e.who)} ${e.helper ? 'came to help!' : 'joined!'}`, 1200); this.host.sound('join'); this.playing = now + 400; break;
      case 'leave': say(`${name(e.who)} left.`, 900); this.playing = now + 300; break;
      case 'say': say(e.text, 1400); this.playing = now + 450; break;
      case 'chain': say(`${e.name}! ${e.n}-hit chain  +${e.bonus}%`, 1100); this.host.sound('chain', 1 + (e.n - 2) * 0.12); this.chainFx = { n: e.n, t0: now }; break;
    }
  }

  private chainFx: { n: number; t0: number } | null = null;
  /** Where each fighter stands on the battle screen (feet, in battle-screen pixels), by formation. */
  private spots(): Map<number, { x: number; y: number; f: Fighter; face: 'left' | 'right' }> {
    const out = new Map<number, { x: number; y: number; f: Fighter; face: 'left' | 'right' }>(), b = this.b; if (!b) return out;
    const foes = b.fighters.filter(f => f.side === 'foe'), party = b.fighters.filter(f => f.side === 'party');
    const col = (list: Fighter[], x0: number, dx: number, face: 'left' | 'right', yMid = 84) => list.forEach((f, i) => {
      const step = list.length > 3 ? 10 : 15, y = yMid + i * step - (list.length - 1) * step / 2; out.set(f.id, { x: x0 + (i % 2) * dx, y, f, face });
    });
    const form = b.formation;
    if (form === 'pincer' || form === 'surround') {
      col(foes, 112, 10, 'right', 86);
      if (form === 'pincer') { col(party.filter(f => f.pos !== 'left'), 176, 10, 'left'); col(party.filter(f => f.pos === 'left'), 48, -10, 'right'); }
      else party.forEach((f, i) => { const a = (i / party.length) * Math.PI * 2; const x = 112 + Math.cos(a) * 64, y = 86 + Math.sin(a) * 24; out.set(f.id, { x, y, f, face: x > 112 ? 'left' : 'right' }); });
    } else {
      col(foes, 58, -12, 'right');
      if (form === 'wall') { col(party.filter(f => f.pos === 'front'), 154, 6, 'left'); col(party.filter(f => f.pos === 'back'), 190, 6, 'left'); }
      else col(party, 168, 12, 'left');
    }
    return out;
  }

  /** True while the intro covers the screen (the world is still drawn underneath it until then). */
  get intro_(): boolean { return !!this.intro && now_() - this.intro.t0 < INTRO_MS; }

  frame(now: number): void {
    if (!this.active) return;
    if (this.intro) this.drawIntro(now);
    if (this.intro && now - this.intro.t0 < INTRO_MS) return;
    this.step(now);
    const c = this.ctx; c.imageSmoothingEnabled = false;
    c.setTransform(1, 0, 0, 1, 0, 0);
    this.drawBackdrop(c, now);
    const spots = this.spots();
    const offset = (id: number): [number, number] => {
      let ox = 0, oy = 0;
      for (const f of this.fx) if (f.kind === 'lunge' && f.who === id) {
        const k = (now - f.t0) / f.dur; if (k < 0 || k > 1) continue;
        const me = spots.get(id), to = f.to != null ? spots.get(f.to) : null;
        const dir = me && to ? Math.sign(to.x - me.x) : 1, s = Math.sin(k * Math.PI);
        ox += dir * s * 16; oy += -s * 3;
      }
      return [ox, oy];
    };
    // everyone, back to front
    const order = [...spots.values()].sort((a, b) => a.y - b.y);
    for (const { x, y, f } of order) {
      const [ox, oy] = offset(f.id), downAt = this.down.get(f.id);
      let alpha = 1; if (f.side === 'foe' && downAt) alpha = Math.max(0, 1 - (now - downAt) / 600);
      if (alpha <= 0) continue;
      c.globalAlpha = alpha;
      c.fillStyle = 'rgba(30,24,30,.25)'; c.beginPath(); c.ellipse(x, y, f.side === 'foe' ? 12 : 8, 2.5, 0, 0, Math.PI * 2); c.fill();
      const flash = this.fx.some(q => q.kind === 'flash' && q.who === f.id && now >= q.t0 && now < q.t0 + q.dur && Math.floor((now - q.t0) / 60) % 2 === 0);
      if (f.side === 'foe') {
        c.save(); c.translate(Math.round(x + ox), Math.round(y + oy)); c.scale(2, 2);
        drawMob(c, { kind: f.mob ?? 'puff', x: 0, y: 0, dir: 1, anim: 0, t: 0, hp: f.hp, max: f.max, hurtAt: flash ? now : 0, deadAt: 0, id: f.id }, now);
        c.restore();
        if (b_isTarget(this, f)) this.cursor(c, x, y - mobTop(f.mob ?? 'puff') * 2 - 6, now);
      } else {
        const look = this.host.look(f.id);
        const lunging = this.fx.some(q => q.kind === 'lunge' && q.who === f.id && now >= q.t0 && now < q.t0 + q.dur);
        const mode: Mode = lunging ? 'a' : f.state === 'down' ? 'i' : 'i';
        const fi = lunging ? Math.min(4, Math.floor((now - this.fx.find(q => q.kind === 'lunge' && q.who === f.id)!.t0) / 80)) : 0;
        const face = spots.get(f.id)!.face, dir = f.state === 'run' ? (face === 'left' ? 'right' : 'left') : face;
        const s = look ? this.host.sprite(f.id, look, dir, mode, fi) : null;
        if (s) {
          if (f.state === 'down') { c.save(); c.translate(Math.round(x + ox), Math.round(y)); c.rotate(-Math.PI / 2); c.drawImage(s, -31, -16); c.restore(); }
          else { if (flash) c.globalAlpha = 0.5; c.drawImage(s, Math.round(x - 16 + ox), Math.round(y - 31 + oy)); c.globalAlpha = alpha; }
        }
        if (f.state === 'defend') { c.fillStyle = '#a3def4'; c.fillRect(Math.round(x - 12), Math.round(y - 22), 3, 6); c.fillStyle = '#2f2a26'; c.fillRect(Math.round(x - 13), Math.round(y - 23), 1, 8); }
        if (this.b?.turn === f.id) this.cursor(c, x, y - 36, now, true);
        if (b_isAllyTarget(this, f)) this.cursor(c, x, y - 36, now);
      }
      c.globalAlpha = 1;
    }
    // floating numbers
    c.font = '8px "Pixelify Sans", "Silkscreen", monospace'; c.textAlign = 'center';
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i], k = (now - f.t0) / f.dur;
      if (k > 1) { this.fx.splice(i, 1); continue; }
      if (k < 0 || f.kind !== 'num') continue;
      const sp = spots.get(f.who); if (!sp) continue;
      const top = sp.f.side === 'foe' ? mobTop(sp.f.mob ?? 'puff') * 2 : 30, y = Math.round(sp.y - top - 4 - k * 10 - Math.sin(Math.min(1, k * 4) * Math.PI) * 4);
      c.globalAlpha = Math.min(1, (1 - k) * 3);
      c.fillStyle = '#2f2a26'; for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) c.fillText(f.text!, sp.x + dx, y + dy);
      c.fillStyle = f.color!; c.fillText(f.text!, sp.x, y);
      c.globalAlpha = 1;
    }
    // a chain: a ring of sparkles pulses out from the creatures
    if (this.chainFx && now - this.chainFx.t0 < 700) {
      const k = (now - this.chainFx.t0) / 700, foe = [...spots.values()].find(q => q.f.side === 'foe' && q.f.hp > 0);
      if (foe) for (let i = 0; i < 10 + this.chainFx.n * 3; i++) { const a = i / (10 + this.chainFx.n * 3) * Math.PI * 2; c.fillStyle = i % 2 ? '#ffe9a8' : '#ffffff'; c.globalAlpha = 1 - k; c.fillRect(Math.round(foe.x + Math.cos(a) * (6 + k * 30)), Math.round(foe.y - 12 + Math.sin(a) * (4 + k * 16)), 2, 2); }
      c.globalAlpha = 1;
    }
    // the banner: what's happening (real text over the picture, so it stays crisp)
    const showBanner = !!this.banner && now < this.bannerUntil && !this.ending, bn = $('bBanner');
    if (bn.hidden === showBanner) bn.hidden = !showBanner;
    if (showBanner && bn.textContent !== this.banner) bn.textContent = this.banner;
    // whose turn, and how long they have
    if (this.myTurn && this.menu) {
      const left = Math.max(0, CHOOSE_TICKS / 60 - (now - this.turnSince) / 1000);
      $('bTimer').textContent = `Your turn · ${Math.ceil(left)}s`; $('bTimer').hidden = false;
    } else $('bTimer').hidden = true;
    c.textAlign = 'left';
  }

  private cursor(c: CanvasRenderingContext2D, x: number, y: number, now: number, me = false): void {
    const bob = Math.floor(now / 250) % 2, X = Math.round(x), Y = Math.round(y) + bob;
    c.fillStyle = '#2f2a26'; c.fillRect(X - 3, Y - 1, 7, 2); c.fillRect(X - 2, Y + 1, 5, 1); c.fillRect(X - 1, Y + 2, 3, 1);
    c.fillStyle = me ? '#ffe9a8' : '#ffffff'; c.fillRect(X - 2, Y - 1, 5, 1); c.fillRect(X - 1, Y, 3, 1); c.fillRect(X, Y + 1, 1, 1);
  }

  private bg: { key: string; cv: HTMLCanvasElement } | null = null;
  private drawBackdrop(c: CanvasRenderingContext2D, now: number): void {
    const b = this.b; if (!b) return;
    const key = `${b.zone}:${b.x}:${b.y}`;
    if (this.bg?.key !== key) {
      const cv = document.createElement('canvas'); cv.width = W; cv.height = H; const g = cv.getContext('2d')!; g.imageSmoothingEnabled = false;
      const bd = this.host.backdrop(b.x, b.y);
      // the field: the zone's own ground near the circle (a dry patch), seen at a slant
      if (bd) for (let y = 46; y < H; y++) {
        const k = (y - 46) / (H - 46), sy = Math.round(bd.y - 30 + k * 60), span = 120 + k * 60;
        g.drawImage(bd.cv, Math.max(0, Math.round(bd.x - span / 2)), Math.max(0, sy), Math.round(span), 1, 0, y, W, 1);
      } else { g.fillStyle = '#9fca6d'; g.fillRect(0, 46, W, H - 46); }
      this.bg = { key, cv };
    }
    const night = this.host.night();
    const sky = c.createLinearGradient(0, 0, 0, 50);
    sky.addColorStop(0, night > 0.5 ? '#1c2244' : '#8cc8ec'); sky.addColorStop(1, night > 0.5 ? '#3a3a6a' : '#d8eef8');
    c.fillStyle = sky; c.fillRect(0, 0, W, 50);
    // far hills and trees
    c.fillStyle = night > 0.5 ? '#2a3a3a' : '#7fae62';
    for (let x = 0; x < W; x++) { const h = 8 + Math.sin(x * 0.045) * 4 + Math.sin(x * 0.13 + 1) * 2; c.fillRect(x, 48 - h, 1, h); }
    c.fillStyle = night > 0.5 ? '#1e2c2a' : '#5e8a4a';
    for (let x = 0; x < W; x += 9) { const h = 6 + ((x * 37) % 5); c.fillRect(x, 48 - h, 7, h); c.fillRect(x + 2, 48 - h - 2, 3, 2); }
    c.drawImage(this.bg!.cv, 0, 46, W, H - 46, 0, 46, W, H - 46);
    c.fillStyle = 'rgba(255,255,255,.18)'; c.fillRect(0, 46, W, 1);
    if (night > 0) { c.fillStyle = `rgba(20,24,60,${night * 0.35})`; c.fillRect(0, 0, W, H); }
    void now;
  }

  /** Which fighters to point at while picking a target. */
  isTarget(f: Fighter): boolean { return this.menu === 'target-foe' && f.side === 'foe'; }
  isAllyTarget(f: Fighter): boolean { return this.menu === 'target-ally' && f.side === 'party' && f.state !== 'down'; }
}
const b_isTarget = (s: BattleScreen, f: Fighter) => s.isTarget(f);
const b_isAllyTarget = (s: BattleScreen, f: Fighter) => s.isAllyTarget(f);

const now_ = () => performance.now();
